import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, withModifiers as _withModifiers, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mall-track-page" }
const _hoisted_2 = { class: "mall-track-page__shell" }
const _hoisted_3 = { class: "mall-track-page__head" }
const _hoisted_4 = { class: "mall-track-page__eyebrow" }
const _hoisted_5 = { class: "mall-track-page__field" }
const _hoisted_6 = ["value"]
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "mall-track-page__field" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  key: 0,
  class: "mall-track-page__error"
}
const _hoisted_11 = { class: "mall-track-page__hero" }
const _hoisted_12 = {
  class: "mall-track-page__hero-icon",
  "aria-hidden": "true"
}
const _hoisted_13 = { class: "mall-track-page__hero-status" }
const _hoisted_14 = { class: "mall-track-page__hero-meta" }
const _hoisted_15 = { class: "mall-track-page__tools" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["href"]
const _hoisted_18 = {
  key: 0,
  class: "mall-track-page__note is-ok"
}
const _hoisted_19 = {
  key: 1,
  class: "mall-track-page__note"
}
const _hoisted_20 = {
  key: 2,
  class: "mall-track-page__timeline-title"
}
const _hoisted_21 = {
  key: 3,
  class: "mall-track-page__list"
}
const _hoisted_22 = { class: "mall-track-page__row-top" }
const _hoisted_23 = {
  key: 0,
  class: "mall-track-page__place"
}
const _hoisted_24 = {
  key: 1,
  class: "mall-track-page__desc"
}
const _hoisted_25 = {
  key: 4,
  class: "mall-track-page__empty"
}

import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchPublicParcelTracking, type MallTracking } from '@/api/mallOrder'
import { MALL_COURIERS, isDirectCourierName } from '@/constants/mallCouriers'
import { MALL_NAME } from '@/constants/mall'
import { copyToClipboard } from '@/utils/clipboard'


export default /*@__PURE__*/_defineComponent({
  __name: 'MallTrackingPage',
  setup(__props) {

const route = useRoute()
const router = useRouter()

const courierOptions = MALL_COURIERS.filter((name) => !isDirectCourierName(name))

const courier = ref('')
const invoice = ref('')
const loading = ref(false)
const copied = ref(false)
const errorMessage = ref('')
const tracking = ref<MallTracking | null>(null)
const resultRef = ref<HTMLElement | null>(null)

const canSearch = computed(() => Boolean(courier.value.trim() && invoice.value.trim()))

const events = computed(() => {
  const list = [...(tracking.value?.events || [])]
  return list.sort((a, b) => {
    const left = new Date(a.at).getTime()
    const right = new Date(b.at).getTime()
    if (Number.isNaN(left) || Number.isNaN(right)) return 0
    return left - right
  })
})

/** 최신 상태가 위로 오도록 (앱 타임라인) */
const eventsReversed = computed(() => [...events.value].reverse())

function formatDateTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value || '-'
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function resolveCourierOption(raw: string) {
  const name = String(raw || '').trim()
  if (!name) return ''
  const exact = courierOptions.find((item) => item === name)
  if (exact) return exact
  const matched = courierOptions.find((item) => {
    if (/CJ|대한통운/i.test(name) && /CJ|대한통운/i.test(item)) return true
    if (/우체국/i.test(name) && /우체국/i.test(item)) return true
    if (/한진/i.test(name) && /한진/i.test(item)) return true
    if (/롯데/i.test(name) && /롯데/i.test(item)) return true
    if (/로젠/i.test(name) && /로젠/i.test(item)) return true
    if (/경동/i.test(name) && /경동/i.test(item)) return true
    if (/대신/i.test(name) && /대신/i.test(item)) return true
    if (/일양/i.test(name) && /일양/i.test(item)) return true
    if (/CU/i.test(name) && /CU/i.test(item)) return true
    if (/GS|포스트박스/i.test(name) && /GS/i.test(item)) return true
    if (/천일/i.test(name) && /천일/i.test(item)) return true
    return false
  })
  return matched || name
}

function applyQuery() {
  const qCourier = String(route.query.courier || '').trim()
  const qInvoice = String(route.query.invoice || '').replace(/\s+/g, '')
  if (qCourier) courier.value = resolveCourierOption(qCourier)
  if (qInvoice) invoice.value = qInvoice
}

async function onSearch() {
  if (!canSearch.value || loading.value) return
  errorMessage.value = ''
  loading.value = true
  try {
    tracking.value = await fetchPublicParcelTracking(courier.value, invoice.value)
    await router.replace({
      query: {
        courier: courier.value.trim(),
        invoice: invoice.value.replace(/\s+/g, ''),
      },
    })
    await nextTick()
    resultRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } catch (error) {
    tracking.value = null
    errorMessage.value =
      error instanceof Error ? error.message : '배송조회를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function copyInvoice() {
  const value = tracking.value?.invoice || invoice.value
  const ok = await copyToClipboard(value)
  if (!ok) return
  copied.value = true
  window.setTimeout(() => {
    copied.value = false
  }, 1600)
}

onMounted(async () => {
  applyQuery()
  if (canSearch.value) {
    await onSearch()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(MALL_NAME)), 1),
        _cache[2] || (_cache[2] = _createElementVNode("h1", null, "배송조회", -1)),
        _cache[3] || (_cache[3] = _createElementVNode("p", null, "로그인 없이 택배사와 송장번호로 배송 현황을 확인할 수 있습니다.", -1))
      ]),
      _createElementVNode("form", {
        class: "mall-track-page__form",
        onSubmit: _withModifiers(onSearch, ["prevent"])
      }, [
        _createElementVNode("label", _hoisted_5, [
          _cache[5] || (_cache[5] = _createElementVNode("span", null, "택배사", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((courier).value = $event)),
            required: ""
          }, [
            _cache[4] || (_cache[4] = _createElementVNode("option", {
              value: "",
              disabled: ""
            }, "선택", -1)),
            (courier.value && !_unref(courierOptions).includes(courier.value as (typeof courierOptions)[number]))
              ? (_openBlock(), _createElementBlock("option", {
                  key: 0,
                  value: courier.value
                }, _toDisplayString(courier.value), 9, _hoisted_6))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(courierOptions), (name) => {
              return (_openBlock(), _createElementBlock("option", {
                key: name,
                value: name
              }, _toDisplayString(name), 9, _hoisted_7))
            }), 128))
          ], 512), [
            [_vModelSelect, courier.value]
          ])
        ]),
        _createElementVNode("label", _hoisted_8, [
          _cache[6] || (_cache[6] = _createElementVNode("span", null, "송장번호", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((invoice).value = $event)),
            type: "text",
            inputmode: "numeric",
            maxlength: "40",
            required: "",
            placeholder: "송장번호 입력",
            autocomplete: "off"
          }, null, 512), [
            [
              _vModelText,
              invoice.value,
              void 0,
              { trim: true }
            ]
          ])
        ]),
        _createElementVNode("button", {
          type: "submit",
          class: "mall-track-page__submit",
          disabled: loading.value || !canSearch.value
        }, _toDisplayString(loading.value ? '조회 중…' : '조회하기'), 9, _hoisted_9)
      ], 32),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(errorMessage.value), 1))
        : _createCommentVNode("", true),
      (tracking.value)
        ? (_openBlock(), _createElementBlock("section", {
            key: 1,
            ref_key: "resultRef",
            ref: resultRef,
            class: "mall-track-page__result",
            tabindex: "-1"
          }, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("span", _hoisted_12, [
                _createVNode(AppIcon, {
                  icon: "mdi-truck-fast-outline",
                  size: 28
                })
              ]),
              _cache[8] || (_cache[8] = _createElementVNode("p", { class: "mall-track-page__hero-label" }, "현재 상태", -1)),
              _createElementVNode("strong", _hoisted_13, _toDisplayString(tracking.value.lastStatus || '조회 완료'), 1),
              _createElementVNode("p", _hoisted_14, [
                _createTextVNode(_toDisplayString(tracking.value.courier || courier.value) + " ", 1),
                _cache[7] || (_cache[7] = _createElementVNode("span", { "aria-hidden": "true" }, "·", -1)),
                _createTextVNode(" " + _toDisplayString(tracking.value.invoice || invoice.value), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("button", {
                type: "button",
                class: "mall-track-page__tool",
                disabled: loading.value,
                onClick: onSearch
              }, [
                _createVNode(AppIcon, {
                  icon: "mdi-refresh",
                  size: 18
                }),
                _cache[9] || (_cache[9] = _createTextVNode(" 다시 조회 ", -1))
              ], 8, _hoisted_16),
              _createElementVNode("button", {
                type: "button",
                class: "mall-track-page__tool",
                onClick: copyInvoice
              }, [
                _createVNode(AppIcon, {
                  icon: "mdi-content-copy",
                  size: 18
                }),
                _cache[10] || (_cache[10] = _createTextVNode(" 송장 복사 ", -1))
              ]),
              (tracking.value.sourceUrl)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    class: "mall-track-page__tool",
                    href: tracking.value.sourceUrl,
                    target: "_blank",
                    rel: "noopener"
                  }, [
                    _createVNode(AppIcon, {
                      icon: "mdi-open-in-new",
                      size: 18
                    }),
                    _cache[11] || (_cache[11] = _createTextVNode(" 택배사 ", -1))
                  ], 8, _hoisted_17))
                : _createCommentVNode("", true)
            ]),
            (copied.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_18, "송장번호를 복사했습니다."))
              : _createCommentVNode("", true),
            (tracking.value.message)
              ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(tracking.value.message), 1))
              : _createCommentVNode("", true),
            (events.value.length)
              ? (_openBlock(), _createElementBlock("h2", _hoisted_20, "배송 진행"))
              : _createCommentVNode("", true),
            (events.value.length)
              ? (_openBlock(), _createElementBlock("ol", _hoisted_21, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(eventsReversed.value, (event, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: `${event.at}-${index}`,
                      class: _normalizeClass({ 'is-latest': index === 0 })
                    }, [
                      _cache[12] || (_cache[12] = _createElementVNode("span", {
                        class: "mall-track-page__dot",
                        "aria-hidden": "true"
                      }, null, -1)),
                      _createElementVNode("div", _hoisted_22, [
                        _createElementVNode("strong", null, _toDisplayString(event.status || '처리'), 1),
                        _createElementVNode("time", null, _toDisplayString(formatDateTime(event.at)), 1)
                      ]),
                      (event.location)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(event.location), 1))
                        : _createCommentVNode("", true),
                      (event.description)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_24, _toDisplayString(event.description), 1))
                        : _createCommentVNode("", true)
                    ], 2))
                  }), 128))
                ]))
              : (!loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                    _createVNode(AppIcon, {
                      icon: "mdi-truck-outline",
                      size: 32
                    }),
                    _cache[13] || (_cache[13] = _createElementVNode("p", null, "아직 표시할 배송일지가 없습니다.", -1))
                  ]))
                : _createCommentVNode("", true)
          ], 512))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})